import React, { useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import api from '@/lib/api';

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  no_show: 'No-show',
  cancelled: 'Cancelled',
};

const ACTION_LABELS = {
  member_created: 'Record created',
  member_updated: 'Record updated',
  lead_converted: 'Converted to member',
  lead_merged: 'Lead merged into this member',
};

const Section = ({ title, count, children }) => (
  <Box>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Chip label={count} size="small" sx={{ height: 18, fontSize: 11 }} />
    </Stack>
    {count === 0 ? (
      <Typography variant="body2" color="text.secondary">
        Nothing yet.
      </Typography>
    ) : (
      <Stack spacing={0.75}>{children}</Stack>
    )}
  </Box>
);

const Row = ({ primary, secondary, right }) => (
  <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
    <Typography variant="body2" noWrap sx={{ flexShrink: 0 }}>
      {primary}
    </Typography>
    <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
      {secondary}
    </Typography>
    {right && (
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {right}
      </Typography>
    )}
  </Stack>
);

// Per-member activity: appointments, invoices, messages, and (for managers)
// the record's audit trail. Read-only; rendered inside MemberDialog's
// History tab.
const MemberHistory = ({ memberId, showChangelog = true }) => {
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [messages, setMessages] = useState([]);
  const [changelog, setChangelog] = useState([]);

  useEffect(() => {
    if (!memberId) return;
    let cancelled = false;
    setLoading(true);
    const params = { params: { member_id: memberId } };
    Promise.all([
      api.get('/appointments', params).then((r) => r.data).catch(() => []),
      api.get('/invoices', params).then((r) => r.data).catch(() => []),
      api.get('/messages', params).then((r) => r.data).catch(() => []),
      showChangelog
        ? api.get(`/members/${memberId}/changelog`).then((r) => r.data).catch(() => [])
        : Promise.resolve([]),
    ]).then(([appts, invs, msgs, log]) => {
      if (cancelled) return;
      setAppointments(appts);
      setInvoices(invs);
      setMessages(msgs);
      setChangelog(log);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [memberId, showChangelog]);

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Section title="Appointments" count={appointments.length}>
        {appointments.map((a) => (
          <Row
            key={a.appointment_id}
            primary={a.service_name || 'Appointment'}
            secondary={STATUS_LABELS[a.status] || a.status}
            right={fmtDateTime(a.start_time)}
          />
        ))}
      </Section>
      <Section title="Invoices" count={invoices.length}>
        {invoices.map((inv) => (
          <Row
            key={inv.invoice_id}
            primary={`$${Number(inv.amount).toFixed(2)}`}
            secondary={`${inv.description || ''} · ${inv.status}`}
            right={fmtDateTime(inv.created_at)}
          />
        ))}
      </Section>
      <Section title="Messages" count={messages.length}>
        {messages.map((msg) => (
          <Row
            key={msg.message_id}
            primary={(msg.channel || '').toUpperCase()}
            secondary={msg.subject || msg.content}
            right={fmtDateTime(msg.sent_at)}
          />
        ))}
      </Section>
      {showChangelog && (
        <Section title="Changes" count={changelog.length}>
          {changelog.map((entry) => {
            const updates = entry.payload?.updates;
            const detail = updates
              ? Object.keys(updates).join(', ')
              : entry.payload?.lead_name
                ? `from lead "${entry.payload.lead_name}"`
                : '';
            return (
              <Row
                key={entry.id}
                primary={ACTION_LABELS[entry.action] || entry.action}
                secondary={[detail, entry.performer_name ? `by ${entry.performer_name}` : '']
                  .filter(Boolean)
                  .join(' · ')}
                right={fmtDateTime(entry.created_at)}
              />
            );
          })}
        </Section>
      )}
    </Stack>
  );
};

export default MemberHistory;
